// ============================================================================
// BOQ — group measurements into trade → sub-trade sections
// ============================================================================
// The Measurements (take-off) sheet lists every measurement in a project. On
// its own that's a flat list; this groups it the same way the priced Bill and
// the Summary do — chapter (trade) → sub-trade — so the QS reads the take-off
// in the same order as the bill (e.g. "1. Site Establishment" → "Housekeeping
// & Cleaning" / "Pest Control", "2. Civil & Structural Works" → …).
//
// A measurement carries no trade of its own; it inherits the section of the
// BOQ line it feeds (its first link). Resolution mirrors ProjectItemsGrid's
// buildTradeSections / SummaryTab's buildTradeRows so all three stay in step:
//   • SD/allowance lines store the canonical chapter/sub-trade label directly
//     (project_items.trade / .subtrade).
//   • library/measured lines roll up to their canonical brio_trade chapter and
//     sub-trade via chapterByItem / subtradeByItem (keyed by item_version code).
//   • anything else falls back to trade_group, raw trade, then code prefix.
// Measurements that feed no line (raw take-off rows not yet linked) collect in
// a trailing "Unlinked" bucket.
// ============================================================================

const MAX = Number.MAX_SAFE_INTEGER;
export const UNLINKED_SECTION_KEY = '__unlinked__';

// Resolve the chapter (section) + sub-trade a single project item belongs to.
function resolveItemSection(it, canonByLabel, chapterByItem, subtradeByItem, tradeGroupByItem) {
  const itemCode = it.version?.itemCode || null;
  const tg = tradeGroupByItem ? tradeGroupByItem.get(itemCode) : null;
  // Canonical chapter: SD lines store the label directly; library lines resolve
  // via chapterByItem so a tender/SD bill organises onto the same chapters.
  const chapter = (it.trade && canonByLabel.has(it.trade))
    ? it.trade
    : (chapterByItem ? chapterByItem.get(itemCode) : null);

  let secKey; let secLabel; let secSort; let canonNum = null;
  if (chapter) {
    secLabel = chapter;
    secKey = `t:${chapter}`;
    const num = canonByLabel.get(chapter);
    canonNum = num != null ? num : null;
    secSort = num != null ? num : (it.sequenceOrder ?? MAX);
  } else if (tg) {
    secKey = `g:${tg.code}`;
    secLabel = tg.label;
    secSort = tg.sortOrder ?? MAX;
  } else if (it.trade) {
    secLabel = it.trade;
    secKey = `t:${it.trade}`;
    const num = canonByLabel.get(it.trade);
    canonNum = num != null ? num : null;
    secSort = num != null ? num : (it.sequenceOrder ?? MAX);
  } else {
    const prefix = (it.version?.fullCode || '').slice(0, 2) || 'Other';
    secKey = `p:${prefix}`;
    secLabel = prefix;
    secSort = MAX;
  }

  // Prefer the canonical brio sub-trade (so library/tender lines read the same
  // as SD); fall back to the line's own sub-trade label.
  const subLabel = (subtradeByItem && subtradeByItem.get(itemCode)) || it.subtrade || '';
  return { secKey, secLabel, secSort, canonNum, subLabel, subSort: it.sequenceOrder ?? MAX };
}

/**
 * Group measurements into ordered chapter → sub-trade sections.
 *
 * @param {Array} measurements  measurement rows (each with links / itemIds)
 * @param {Array} items         the project's BOQ lines (project_items)
 * @param {Object} opts
 *   canonicalTrades   [{label, sortOrder}] | null  — canonical chapter order
 *   chapterByItem     Map itemCode → chapter label
 *   subtradeByItem    Map itemCode → sub-trade label
 *   tradeGroupByItem  Map itemCode → { code, label, sortOrder }
 * @returns {Array} sections, each:
 *   { key, label, count, isUnlinked,
 *     bands: [{ key, label, rows }],   // sub-trade bands (may be empty)
 *     directRows }                     // rows shown straight under the chapter
 */
export function groupMeasurementsBySubtrade(measurements, items, opts = {}) {
  const { canonicalTrades, chapterByItem, subtradeByItem, tradeGroupByItem } = opts;

  const canonByLabel = new Map();
  if (canonicalTrades && canonicalTrades.length > 0) {
    canonicalTrades.forEach((t, i) => canonByLabel.set(t.label, i + 1));
  }

  const itemById = new Map();
  (items || []).forEach((it) => itemById.set(it.id, it));

  const sections = new Map();
  const ensureSection = (key, label, sort, canonNum) => {
    if (!sections.has(key)) {
      sections.set(key, { key, label, sort, canonNum: canonNum ?? null, subs: new Map(), directRows: [] });
    }
    return sections.get(key);
  };

  (measurements || []).forEach((m) => {
    const primaryId = (m.links && m.links[0] && m.links[0].itemId)
      ?? (m.itemIds && m.itemIds[0])
      ?? null;
    const it = primaryId != null ? itemById.get(primaryId) : null;

    if (!it) {
      ensureSection(UNLINKED_SECTION_KEY, 'Unlinked — not tied to a line', MAX, null).directRows.push(m);
      return;
    }

    const { secKey, secLabel, secSort, canonNum, subLabel, subSort } =
      resolveItemSection(it, canonByLabel, chapterByItem, subtradeByItem, tradeGroupByItem);
    const sec = ensureSection(secKey, secLabel, secSort, canonNum);
    if (secSort < sec.sort) sec.sort = secSort;

    if (subLabel) {
      const subKey = `${secKey}::sub::${subLabel}`;
      if (!sec.subs.has(subKey)) sec.subs.set(subKey, { key: subKey, label: subLabel, sort: subSort, rows: [] });
      const band = sec.subs.get(subKey);
      band.rows.push(m);
      if (subSort < band.sort) band.sort = subSort;
    } else {
      sec.directRows.push(m);
    }
  });

  const bySort = (a, b) => (a.sort !== b.sort ? a.sort - b.sort : String(a.label).localeCompare(String(b.label)));

  const ordered = [...sections.values()].sort((a, b) => {
    // Unlinked always sinks to the bottom regardless of its sort key.
    if (a.key === UNLINKED_SECTION_KEY) return 1;
    if (b.key === UNLINKED_SECTION_KEY) return -1;
    return bySort(a, b);
  });

  return ordered.map((sec) => {
    const bands = [...sec.subs.values()].sort(bySort);
    // Only band when there's real sub-trade structure — a lone bucket that's
    // unlabelled or just repeats the chapter name gets no band (mirrors the
    // Bill grid). Its rows fall straight under the chapter header instead.
    const hasBands = bands.length > 1
      || (bands.length === 1 && !!bands[0].label && bands[0].label !== sec.label);
    const count = sec.directRows.length + bands.reduce((n, b) => n + b.rows.length, 0);
    const label = sec.canonNum ? `${sec.canonNum}. ${sec.label}` : sec.label;

    if (hasBands) {
      return { key: sec.key, label, count, isUnlinked: sec.key === UNLINKED_SECTION_KEY, bands, directRows: sec.directRows };
    }
    const directRows = [...sec.directRows, ...bands.flatMap((b) => b.rows)];
    return { key: sec.key, label, count, isUnlinked: sec.key === UNLINKED_SECTION_KEY, bands: [], directRows };
  });
}

export default groupMeasurementsBySubtrade;
